// Time and ordering rules of the Scan in/out page, kept free of Vue so they
// can be tested on their own.

const pad = (value) => String(value).padStart(2, '0');

export const formatDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
};

/**
 * A finished stretch of time in words ("17 min", "1 h 7 min"). Totals read
 * this way so they are not mistaken for a clock time next to "8:29 am".
 */
export const formatSpan = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));

  if (totalSeconds < 60) {
    return `${totalSeconds} sec`;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (!hours) {
    return `${minutes} min`;
  }

  return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
};

/**
 * Timers count from server timestamps, so they must also read the time on
 * the server's clock: a phone running minutes behind would otherwise show a
 * shorter (or, clamped, zeroed) time in the room.
 */
export const createServerClock = () => {
  let offset = 0;

  return {
    sync(serverNowIso, receivedAt = Date.now()) {
      const serverNow = Date.parse(serverNowIso);

      if (!Number.isNaN(serverNow)) {
        offset = serverNow - receivedAt;
      }
    },

    now() {
      return Date.now() + offset;
    },
  };
};

const toTime = (iso) => {
  const time = Date.parse(iso);

  return Number.isNaN(time) ? null : time;
};

export const normalizeVisit = (item) => ({
  sessionId: item.session_id,
  sessionName: item.session_name,
  signedInAt: toTime(item.signed_in_at),
  signedInTime: item.signed_in_time || '',
  sessionTime: item.session_time || '',
  room: item.room || '',
  sessionEndAt: toTime(item.session_end_at),
  sessionEndTime: item.session_end_time || '',
  signOutUntil: toTime(item.sign_out_until),
  signOutUntilTime: item.sign_out_until_time || '',
  canSignOut: item.can_sign_out !== false,
  // Closed visits of this session: the live timer continues from this total.
  earlierSeconds: Number(item.earlier_seconds) || 0,
  visits: item.visits || [],
});

const canStillSignOut = (visit, now) => visit.canSignOut
  && (visit.signOutUntil === null || visit.signOutUntil > now);

/**
 * The newest visit that a scan can still close is the room the attendee is
 * in now; every other open visit is one they forgot to sign out of.
 */
export const orderVisits = (visits, now) => {
  const newestFirst = visits.slice().sort((a, b) => (b.signedInAt || 0) - (a.signedInAt || 0));
  const live = newestFirst.find((visit) => canStillSignOut(visit, now)) || null;

  const stale = newestFirst
    .filter((visit) => visit !== live)
    .map((visit) => ({
      ...visit,
      expired: !canStillSignOut(visit, now),
      ended: visit.sessionEndAt !== null && visit.sessionEndAt <= now,
    }));

  return { live, stale };
};

// Median calls these globals when the app returns from the background; the
// previous handlers keep working (helpers/camera.js chains them too).
const resumedCallbacks = new Set();
let resumedInstalled = false;

const installResumed = () => {
  if (resumedInstalled) {
    return;
  }

  resumedInstalled = true;

  ['median_app_resumed', 'gonative_app_resumed'].forEach((name) => {
    const previous = window[name];

    window[name] = function (...args) {
      resumedCallbacks.forEach((callback) => callback());

      return typeof previous === 'function' ? previous.apply(this, args) : undefined;
    };
  });
};

export const onAppResumed = (callback) => {
  installResumed();
  resumedCallbacks.add(callback);

  return () => resumedCallbacks.delete(callback);
};
